import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { closeFeedback } from '@/stores/feedback-slice';
import { submitFeedbackThunk } from '@/stores/thunks/submit-feedback-thunk';
import type { FeedbackDocument } from '@syfo/types/feedback.types';
import { FeedbackType, FeedbackConstraints } from '@syfo/types/feedback.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PrimaryButton } from '@/components/ui/primary-button';
import { SecondaryButton } from '@/components/ui/secondary-button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Loader2, CheckCircle2 } from 'lucide-react';

export const FeedbackDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, isSending, error } = useAppSelector(state => state.feedback);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(FeedbackType.SUGGESTION);
  const [message, setMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) return;

    const feedback: FeedbackDocument = {
      type: feedbackType,
      content: message.trim(),
    };

    try {
      await dispatch(submitFeedbackThunk({ feedback })).unwrap();
      setShowSuccess(true);
      setMessage('');
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const handleClose = () => {
    dispatch(closeFeedback());
    setFeedbackType(FeedbackType.SUGGESTION);
    setMessage('');
    setShowSuccess(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={ e => e.preventDefault() }>
        {!showSuccess ? (
          <>
            <DialogHeader>
              <DialogTitle>Send Feedback</DialogTitle>
              <DialogDescription>
                Help us improve by sharing your thoughts, suggestions, or reporting issues.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="feedback-type">Type</Label>
                  <NativeSelect
                    id="feedback-type"
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                    disabled={isSending}
                  >
                    <NativeSelectOption value={FeedbackType.BUG}>Bug Report</NativeSelectOption>
                    <NativeSelectOption value={FeedbackType.SUGGESTION}>Suggestion</NativeSelectOption>
                    <NativeSelectOption value={FeedbackType.COMPLIMENT}>Compliment</NativeSelectOption>
                    <NativeSelectOption value={FeedbackType.OTHER}>Other</NativeSelectOption>
                  </NativeSelect>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="feedback-message">
                    Message
                    <span className="text-xs text-muted-foreground ml-2">
                      ({message.length}/{FeedbackConstraints.document.content.maxLength})
                    </span>
                  </Label>
                  <Textarea
                    id="feedback-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what you think..."
                    rows={6}
                    disabled={isSending}
                    maxLength={FeedbackConstraints.document.content.maxLength}
                  />
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}
              </div>

              <DialogFooter>
                <SecondaryButton
                  type="button"
                  onClick={handleClose}
                  disabled={isSending}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!message.trim() || isSending}
                >
                  {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Feedback
                </PrimaryButton>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Thanks for your feedback!
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-muted-foreground">
                Your feedback has been received. We appreciate you taking the time to help us improve SYFO.
              </p>
            </div>

            <div className="flex justify-end">
              <PrimaryButton onClick={handleClose}>
                Close
              </PrimaryButton>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
